import { supabase } from '../lib/supabase';
import type {
  BadgeDefinition,
  UserBadge,
  UserStreak,
  GamificationStats,
  BadgeProgress,
  CategoryProgress,
  BadgeCategory,
} from '../types/gamification';
import { logger } from '../lib/logger';

// ==========================================
// Badge Definitions
// ==========================================

export async function getBadgeDefinitions(): Promise<BadgeDefinition[]> {
  const { data, error } = await supabase
    .from('badge_definitions')
    .select('*')
    .order('category')
    .order('tier');

  if (error) throw error;
  return data || [];
}

export async function getBadgeDefinitionsByCategory(category: BadgeCategory): Promise<BadgeDefinition[]> {
  const { data, error } = await supabase
    .from('badge_definitions')
    .select('*')
    .eq('category', category)
    .order('tier');

  if (error) throw error;
  return data || [];
}

// ==========================================
// User Badges
// ==========================================

export async function getUserBadges(userId: string): Promise<UserBadge[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select(`
      *,
      badge:badge_definitions(*)
    `)
    .eq('user_id', userId)
    .order('earned_at', { ascending: false });

  if (error) throw error;
  return data || [];
}

export async function awardBadge(userId: string, badgeId: string): Promise<UserBadge | null> {
  const { data, error } = await supabase
    .from('user_badges')
    .insert({ user_id: userId, badge_id: badgeId })
    .select(`
      *,
      badge:badge_definitions(*)
    `)
    .single();

  // Ignore duplicate errors (badge already awarded)
  if (error?.code === '23505') return null;
  if (error) throw error;
  return data;
}

export async function hasUserBadge(userId: string, badgeId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from('user_badges')
    .select('id')
    .eq('user_id', userId)
    .eq('badge_id', badgeId)
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

// ==========================================
// User Streaks
// ==========================================

export async function getUserStreak(userId: string): Promise<UserStreak | null> {
  const { data, error } = await supabase
    .from('user_streaks')
    .select('*')
    .eq('user_id', userId)
    .maybeSingle();

  if (error) throw error;
  return data;
}

export async function trackDailyLogin(userId: string): Promise<{ streak: UserStreak; newBadges: UserBadge[] }> {
  const today = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  // 1. Try to insert today's login session (ignore if duplicate)
  const { error: sessionError } = await supabase
    .from('user_login_sessions')
    .insert({ user_id: userId, login_date: today });

  // Ignore duplicate errors (23505 = unique violation, 409 = conflict)
  if (sessionError && sessionError.code !== '23505' && sessionError.code !== 'PGRST116') {
    // Only throw for real errors, not duplicates
    logger.debug('Login session already exists for today');
  }

  // 2. Get or create streak record
  let streak = await getUserStreak(userId);

  if (!streak) {
    // Create initial streak record
    const { data: newStreak, error: createError } = await supabase
      .from('user_streaks')
      .insert({
        user_id: userId,
        current_streak: 1,
        longest_streak: 1,
        last_active_date: today,
      })
      .select()
      .single();

    if (createError) throw createError;
    streak = newStreak;
  } else {
    // Calculate streak based on last active date
    const lastActive = streak.last_active_date;
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    const yesterdayStr = yesterday.toISOString().split('T')[0];

    let newCurrentStreak = streak.current_streak;

    if (lastActive === today) {
      // Already logged in today, no change
    } else if (lastActive === yesterdayStr) {
      // Consecutive day, increment streak
      newCurrentStreak = streak.current_streak + 1;
    } else {
      // Streak broken, reset to 1
      newCurrentStreak = 1;
    }

    const newLongestStreak = Math.max(streak.longest_streak, newCurrentStreak);

    // Update streak record
    const { data: updatedStreak, error: updateError } = await supabase
      .from('user_streaks')
      .update({
        current_streak: newCurrentStreak,
        longest_streak: newLongestStreak,
        last_active_date: today,
      })
      .eq('user_id', userId)
      .select()
      .single();

    if (updateError || !updatedStreak) throw updateError || new Error('Failed to update streak');
    streak = updatedStreak;
  }

  // 3. Check for new streak badges
  const newBadges = await checkAndAwardStreakBadges(userId, streak!.current_streak);

  return { streak: streak!, newBadges };
}

// ==========================================
// Gamification Stats
// ==========================================

export async function getGamificationStats(userId: string): Promise<GamificationStats> {
  // Get leads count (leads from form submissions)
  const { count: leadsCount } = await supabase
    .from('leads')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('source', 'FORM');

  // Get page views count (total views across all user's pages)
  const { data: pageViewsData } = await supabase
    .from('page_events')
    .select('id')
    .eq('user_id', userId)
    .eq('event_type', 'PAGE_VIEW');

  // Get completed todos count
  const { count: todosCount } = await supabase
    .from('todos')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('completed', true);

  // Get streak data
  const streak = await getUserStreak(userId);

  return {
    totalLeads: leadsCount || 0,
    totalPageViews: pageViewsData?.length || 0,
    totalCompletedTodos: todosCount || 0,
    currentStreak: streak?.current_streak || 0,
    longestStreak: streak?.longest_streak || 0,
  };
}

// ==========================================
// Badge Progress
// ==========================================

export async function getBadgeProgress(userId: string): Promise<CategoryProgress[]> {
  const [badges, userBadges, stats] = await Promise.all([
    getBadgeDefinitions(),
    getUserBadges(userId),
    getGamificationStats(userId),
  ]);

  const earnedBadgeIds = new Set(userBadges.map((ub) => ub.badge_id));
  const earnedBadgeMap = new Map(userBadges.map((ub) => [ub.badge_id, ub.earned_at]));

  const categoryValues: Record<BadgeCategory, number> = {
    leads: stats.totalLeads,
    page_views: stats.totalPageViews,
    todos: stats.totalCompletedTodos,
    streak: stats.currentStreak,
  };

  const categories: BadgeCategory[] = ['leads', 'page_views', 'todos', 'streak'];
  const result: CategoryProgress[] = [];

  for (const category of categories) {
    const categoryBadges = badges.filter((b) => b.category === category);
    const currentValue = categoryValues[category];

    const badgeProgress: BadgeProgress[] = categoryBadges.map((badge) => ({
      badge,
      current: currentValue,
      earned: earnedBadgeIds.has(badge.id),
      earnedAt: earnedBadgeMap.get(badge.id),
    }));

    result.push({
      category,
      badges: badgeProgress,
      currentValue,
    });
  }

  return result;
}

// ==========================================
// Badge Awarding
// ==========================================

async function checkAndAwardStreakBadges(userId: string, currentStreak: number): Promise<UserBadge[]> {
  const streakBadges = await getBadgeDefinitionsByCategory('streak');
  const newBadges: UserBadge[] = [];

  for (const badge of streakBadges) {
    if (currentStreak >= badge.threshold) {
      const awarded = await awardBadge(userId, badge.id);
      if (awarded) newBadges.push(awarded);
    }
  }

  return newBadges;
}

export async function checkAndAwardBadges(
  userId: string,
  category: BadgeCategory
): Promise<UserBadge[]> {
  const stats = await getGamificationStats(userId);
  const badges = await getBadgeDefinitionsByCategory(category);
  const newBadges: UserBadge[] = [];

  const categoryValues: Record<BadgeCategory, number> = {
    leads: stats.totalLeads,
    page_views: stats.totalPageViews,
    todos: stats.totalCompletedTodos,
    streak: stats.currentStreak,
  };

  const currentValue = categoryValues[category];

  for (const badge of badges) {
    if (currentValue >= badge.threshold) {
      const awarded = await awardBadge(userId, badge.id);
      if (awarded) newBadges.push(awarded);
    }
  }

  return newBadges;
}

export async function checkAllBadges(userId: string): Promise<UserBadge[]> {
  const categories: BadgeCategory[] = ['leads', 'page_views', 'todos', 'streak'];
  const allNewBadges: UserBadge[] = [];

  for (const category of categories) {
    const newBadges = await checkAndAwardBadges(userId, category);
    allNewBadges.push(...newBadges);
  }

  return allNewBadges;
}

// ==========================================
// Recent & Next Badges (for Dashboard Widget)
// ==========================================

export async function getRecentBadges(userId: string, limit = 4): Promise<UserBadge[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select(`
      *,
      badge:badge_definitions(*)
    `)
    .eq('user_id', userId)
    .order('earned_at', { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data || [];
}

export async function getNextBadges(userId: string, limit = 4): Promise<BadgeProgress[]> {
  const progress = await getBadgeProgress(userId);
  const nextBadges: BadgeProgress[] = [];

  for (const category of progress) {
    for (const bp of category.badges) {
      if (!bp.earned && nextBadges.length < limit) {
        nextBadges.push(bp);
      }
    }
  }

  // Sort by closest to completion
  nextBadges.sort((a, b) => {
    const progressA = a.current / a.badge.threshold;
    const progressB = b.current / b.badge.threshold;
    return progressB - progressA;
  });

  return nextBadges.slice(0, limit);
}
